import _ from 'lodash';
import React from 'react';
import Creatable from 'react-select/creatable';
import { connect } from 'react-redux';
import { Styles } from 'react-select';
import { useTranslation } from 'react-i18next';

import { isEnumMember } from 'common/dist/utils/types';
import { Option } from './Type';
import * as api from '../../../api';
import { FormSetSelected } from '../../../reducers/store';
import { getSelectedPracticePreference } from '../../../utils/formSet';
import IndicatorsContainer from './IndicatorsContainer';
import {
  Shade00Color,
  Shade20Color,
  Shade40Color,
  Shade70Color,
  Shade80Color,
} from 'common/dist/styles/common';
import { getOptionsIntersection, isTruthy } from '../../utils';

const selectStyle: Styles<Option, false> = {
  dropdownIndicator: () => ({
    display: 'none',
  }),
  valueContainer: () => ({
    padding: '7px 16px 7px 16px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: () => ({
    boxShadow: '0px 0px 16px 0px #2527520D',
    top: '100%',
    backgroundColor: Shade00Color,
    borderRadius: '4px',
    border: `1px solid ${Shade40Color}`,
    marginBottom: '8px',
    marginTop: '8px',
    position: 'absolute',
    width: '100%',
    zIndex: '3',
    left: '-1px',
  }),
  menuList: () => ({
    paddingTop: '0px',
    paddingBottom: '0px',
  }),
  option: (provided, { isSelected, isFocused }) => ({
    ...provided,
    'color': Shade80Color,
    'backgroundColor': isSelected
      ? Shade00Color
      : isFocused
        ? Shade20Color
        : Shade00Color,
    'fontWeight': isSelected ? '600' : undefined,
    ':active': { backgroundColor: Shade20Color },
    ':hover': { backgroundColor: Shade20Color },
  }),
  placeholder: (provided) => ({
    ...provided,
    fontStyle: 'italic',
    fontWeight: '400',
  }),
};

interface StateProps {
  employers: api.Employer.Employer[];
}

interface Props extends StateProps {
  id: string;
  value: Partial<api.Employer.EmployerFormFieldValue>;
  onChange: (value: Partial<api.Employer.EmployerFormFieldValue>) => void;
}

const splitAndUpperFirst = (str: string): string => {
  return str.split('_').map(_.capitalize).join(' ');
};

const EmployerInput: React.FC<Props> = ({ id, value, onChange, employers }) => {
  const { t } = useTranslation();
  const options = _([
    value.employerRaw,
    ...getOptionsIntersection(api.Employer.Employer, employers),
  ])
    .uniq()
    .filter(isTruthy)
    .value();
  const handleChange = (e: Option | null) => {
    if (!e) {
      return onChange({ employer: undefined, employerRaw: undefined });
    }

    const capitalizedValue = e.value.toUpperCase();
    const employer =
      isEnumMember(api.Employer.Employer, capitalizedValue) &&
      options.includes(capitalizedValue)
        ? capitalizedValue
        : api.Employer.Employer.Other;
    const employerRaw = e.value;
    onChange({ employer, employerRaw });
  };
  const displayOptions = options.map((e) => ({
    label: splitAndUpperFirst(e),
    value: e,
  }));
  const displayValue = value.employerRaw
    ? {
        label: splitAndUpperFirst(value.employerRaw),
        value: value.employerRaw,
      }
    : undefined;
  const renderCreateLabel = (inputValue: string) => {
    return (
      <div
        style={{
          fontWeight: '600',
          color: Shade70Color,
        }}
      >
        Add: "{inputValue}"
      </div>
    );
  };
  return (
    <Creatable
      id={id}
      inputId={`${id}-input`}
      isClearable
      options={displayOptions}
      onChange={handleChange}
      styles={selectStyle}
      isMulti={false}
      value={displayValue}
      formatCreateLabel={renderCreateLabel}
      noOptionsMessage={() => null}
      placeholder={t('Select or add an employer')}
      createOptionPosition="first"
      components={{ IndicatorsContainer }}
    />
  );
};

function mapStateToProps(store: FormSetSelected): StateProps {
  return {
    employers: getSelectedPracticePreference(store).employers,
  };
}

export default connect(mapStateToProps)(EmployerInput);
